import React from "react";
import {Unsubscribe} from "redux";
import {SpinnerLoader} from "scuid-x";
import {HeaderAndFooter} from "../components/HeaderAndFooter";
import {ApiCalls, getIdUploadStatus, sendBackendEvent, setDeviceTags, setIdUploadStatus} from "../constants/ApiCalls";
import {DocType} from "../constants/DocType";
import {ApiErrors} from "../constants/Errors";
import {FlowStates} from "../constants/FlowStates";
import {PubSubEvents} from "../constants/PubSubEvents";
import {Routes} from "../constants/Routing";
import {DcStore} from "../stores/DcStore";
import * as document from "./ManualUploadExample";
import "./ManualUpload.css";
import {ErrorCode, FileRejection} from "react-dropzone";
import {PopupModal} from "./PopupModal";
import {DragAndDrop} from "./UploadFile/DragAndDrop";
import {Logger} from "../constants/Logger";
import {IDVMethod} from "../constants/IDVMethod";
import {IdUploadStatus} from "../constants/IdUploadStatus";
import {MeasurementEvents} from "../constants/Measurement";

export interface ManualUploadProps {
  appId?: string;
  coBorrower: string | null;
  uuid?: string;
  docType?: string;
  attemptedVeriff?: boolean;
}

export interface ManualUploadState {
  docIndex: number;
  docTypes: DocType[];
  documentUploading?: boolean;
  errorState: boolean;
  fileUploadErrorMsg: string;
  isLoading: boolean;
  store: DcStore;
}

export interface IdentityDoc {
  fileName: string;
  doc: string;
  captureType: DocType;
}

export class ManualUpload extends React.Component<ManualUploadProps, ManualUploadState> {
  private readonly unsubscribe: Unsubscribe;
  private defaultFileUploadErrorMsg = "File must be JPG, PNG or PDF and smaller than 25MB.";
  private defaultFilesizeErrorMsg = "File must be smaller than 25MB.";
  private defaultFileTypeErrorMsg = "File must be JPG, PNG, or PDF.";
  constructor(props: ManualUploadProps) {
    super(props);
    const store = new DcStore();
    this.unsubscribe = store.subscribe(() => this.onStoreChange());
    const docTypes = store.getState().documents;

    this.state = {
      docIndex: 0,
      docTypes: !!docTypes ? docTypes : [],
      errorState: false,
      fileUploadErrorMsg: this.defaultFileUploadErrorMsg,
      isLoading: true,
      store
    };
  }

  public async componentDidMount() {
    const idUploadStatus = await getIdUploadStatus(this.props.uuid, this.props.appId,
        this.props.coBorrower, this.props.docType);
    if (idUploadStatus?.status === IdUploadStatus.FINISHED) {
      Routes.goToFinished();
      return;
    }
    await this.state.store.apiActions.getReqDocs(this.props.uuid, this.props.appId,
        this.props.coBorrower, false, this.props.docType, false);
    await Promise.all([
      setIdUploadStatus(this.props.uuid, this.props.appId, this.props.coBorrower, this.props.docType,
          IdUploadStatus.STARTED).then().catch((err) => {
        Logger.error("Failed to set id upload status to STARTED for Manual Upload.", err);
      }),
      setDeviceTags(this.props.uuid, this.props.appId, this.props.coBorrower, this.props.docType,
          IDVMethod.MANUAL, IdUploadStatus.STARTED, navigator.mediaDevices.enumerateDevices()).catch((error) => {
        Logger.error("Error setting device tags for Manual Upload", error);
      })
    ]);
  }

  public componentWillUnmount() {
    this.unsubscribe();
  }

  /**
   * Sets the Finished ID Upload status and goes to Finished route.
   * @private
   */
  private sessionFinished() {
    (async () => {
      await setIdUploadStatus(this.props.uuid, this.props.appId, this.props.coBorrower, this.props.docType,
          IdUploadStatus.FINISHED).catch((err) => {
        Logger.error("Failed to set id upload status to finished for Manual Upload.", err);
      });
      await setDeviceTags(this.props.uuid, this.props.appId, this.props.coBorrower, this.props.docType,
          IDVMethod.MANUAL, IdUploadStatus.FINISHED, navigator.mediaDevices.enumerateDevices()).catch((error) => {
        Logger.error("Error setting device tags for Manual Upload", error);
      });
      // if co-borrower -> send co-borrower event;
      // if primary borrower -> send escape hatch event if attempted Veriff);
      //                        else send not selected event.
      // this event includes: ios app fails browser/version check, camera problem
      const eventName = this.props.coBorrower === "true"? MeasurementEvents.EV_ID_COBORROWER_MANUAL_UPLOAD :
              (this.props.attemptedVeriff? MeasurementEvents.EV_ID_ESCAPE_HATCH :
              MeasurementEvents.EV_ID_PRIMARY_VERIFF_NOT_SELECTED);
      sendBackendEvent(this.props.uuid, this.props.appId, eventName, this.props.coBorrower);
      Routes.goToFinished();
      return;
    })();
  }

  private onStoreChange() {
    const docTypes = this.state.store.getState().documents;
    if (this.state.docTypes !== docTypes) {
      if (docTypes == null || docTypes.length <= 0) {
        this.sessionFinished();
        return;
      }
      this.setState({
        docIndex: 0,
        docTypes,
        isLoading: false,
      });
    }
  }

  /**
   * Validates and Uploads the document uploaded/dropped to the Dropzone.
   * If there are no accepted files uploaded and at least one rejected file, it will show the default error message.
   * If the user chooses an invalid file (only one), show message based on the error code.
   * @param captureType Document type uploaded by the user
   * @param accepted Files passed by react-dropzone
   * @param rejected Rejected files passed by react-dropzone
   */
  private uploadDocument = (captureType: DocType, accepted: File[], rejected: FileRejection[]) => {
    const { docIndex, docTypes, isLoading } = this.state;
    if (isLoading) {
      return;
    }

    if (rejected.length > 0 && accepted.length === 0) {
      let errorMsg = this.defaultFileUploadErrorMsg;
      if (rejected.length === 1) {
        const errorCode = rejected[0].errors[0].code;
        if (errorCode === ErrorCode.FileTooLarge) {
          errorMsg = this.defaultFilesizeErrorMsg;
        } else if (errorCode === ErrorCode.FileInvalidType) {
          errorMsg = this.defaultFileTypeErrorMsg;
        }
      }
      this.setState({
        errorState: true,
        documentUploading: false,
        fileUploadErrorMsg: errorMsg
      });
      return;
    }
    this.setState({
      documentUploading: true,
    });

    if (docIndex > (docTypes.length - 1)) {
      this.sessionFinished();
      return;
    }

    const reader = new FileReader();

    if (accepted.length > 0) {
      // Parse and upload the first accepted file from the uploaded files.
      const doc = accepted[0];
        const onLoaderDone = () => {
          const file: IdentityDoc = {
            captureType,
            doc: reader.result as string,
            fileName: doc.name,
          };
          if (this.acceptedType(file.captureType)) {
            ApiCalls.manualUploadDocument(
              this.props.uuid,
              this.props.appId,
              this.props.coBorrower,
              file.captureType,
              file.doc,
              file.fileName,
              (response) => {
                this.setState({
                  documentUploading: false,
                });
                this.handleUploadComplete();
              },
              (error, data) => {
                this.setState({
                  documentUploading: false,
                });

                switch (error) {
                  case ApiErrors.MISMATCHED_CONTENT_TYPE: {
                    this.setState({
                      errorState: true,
                      fileUploadErrorMsg: "File must be JPG, JPEG, PNG, or PDF.",
                    });
                    break;
                  }
                  case ApiErrors.ALREADY_VALID: {
                    this.handleUploadComplete();
                    break;
                  }
                  case ApiErrors.DOC_TYPE_NOT_FOUND: {
                    this.handleUploadComplete();
                    break;
                  }
                  case ApiErrors.LOGGED_OUT: {
                    PubSubEvents.setFlowState(FlowStates.LOGGED_OUT);
                    break;
                  }
                  case ApiErrors.ANON_ID_EXPIRED: {
                    Routes.expiredLinkLoginRedirect(data.appId, data.coBorrower);
                    break;
                  }
                  case ApiErrors.ANON_ID_NOT_FOUND: {
                    PubSubEvents.setFlowState(FlowStates.UUID_NOT_FOUND);
                    break;
                  }
                  default: {
                    this.setState({
                      errorState: true,
                      fileUploadErrorMsg: "Something went wrong, try uploading again.",
                    });
                    break;
                  }
                }
              },
            );
          }
        };
        reader.addEventListener("load", onLoaderDone, false);
        reader.readAsDataURL(doc);
      }
  };

  private acceptedType(captureType: DocType): boolean {
    for (const type of this.state.docTypes!!) {
      if (captureType === type) {
        return true;
      }
    }
    return false;
  }

  private handleUploadError = () => {
    this.setState({
      errorState: false,
    });
  };

  private handleUploadComplete = () => {
    const { docIndex, docTypes }  = this.state;

    if (docIndex >= docTypes.length - 1) {
      this.sessionFinished();
      return;
    }

    this.setState({
      docIndex: docIndex + 1,
    });

    this.state.store.apiActions.getReqDocs(this.props.uuid, this.props.appId,
        this.props.coBorrower, false, this.props.docType, false);
  };

  public render() {
    const { docTypes, docIndex, isLoading } = this.state;

    let loader = null;
    if (isLoading) {
      return <SpinnerLoader isFullPage={true}/>;
    } else if (this.state.documentUploading) {
      loader = <SpinnerLoader isFullPage={true}/>;
    }

    let error = null;
    if (this.state.errorState) {
      error = (
        <PopupModal
          prompt={this.state.fileUploadErrorMsg}
          title={"Error Uploading File"}
          onClick={this.handleUploadError}
        />
      );
    }

    // docs are guaranteed to be back then front for auto, but business wants front then back.
    const currentDoc = docTypes[docTypes.length - (docIndex + 1)];
    let results : any = {};
    const pronoun = this.props.coBorrower === "true" ? "your Co-Borrower's" : "your";
    switch (currentDoc){
      case DocType.LICENSE_FRONT: {
        results = {
          dragAndDropTitle: "Add the front of your ID.",
          example: (
              <document.DriverLicenseManualUploadExample
                title={`Add the front side of ${pronoun} Driver's License, first.`}
                description={"We need to see all four corners of your document, and make sure that all information is visible."}
                docType={currentDoc}
              />
          ),
        };
        break;
      }
      case DocType.LICENSE_BACK: {
        results = {
          dragAndDropTitle: "Add the back of your ID.",
          example: (
              <document.DriverLicenseManualUploadExample
                title={`Add the back side of ${pronoun} Driver's License.`}
                description={"We need to see all four corners of your document, and make sure that all information is visible."}
                docType={currentDoc}
              />
          ),
        };
        break;
      }
      case DocType.PASSPORT: {
        results = {
          dragAndDropTitle: "Add your ID.",
          example: (
              <document.PassportManualUploadExample
                title={`Add ${pronoun} US Passport.`}
                description={"We need to see all four corners of your document, and make sure that all information is visible."}
                docType={currentDoc}
              />
          ),
        };
        break;
      }
      case DocType.STATE_ID_FRONT: {
        results = {
          dragAndDropTitle: "Add the front of your ID.",
          example: (
              <document.StateIdManualUploadExample
                title={`Add the front side of ${pronoun} State ID.`}
                description={"We need to see all four corners of your document, and make sure that all information is visible."}
                docType={currentDoc}
              />
          ),
        };
        break;
      }
      case DocType.STATE_ID_BACK: {
        results = {
          dragAndDropTitle: "Add the back of your ID.",
          example: (
              <document.StateIdManualUploadExample
                title={`Add the back side of ${pronoun} State ID.`}
                description={"We need to see all four corners of your document, and make sure that all information is visible."}
                docType={currentDoc}
              />
          ),
        };
        break;
      }
      case DocType.GREEN_CARD_FRONT: {
        results = {
          dragAndDropTitle: "Add the front of your ID.",
          example: (
              <document.GreenCardManualUploadExample
                  title={`Add the front side of ${pronoun} Green Card, first.`}
                  description={"We need to see all four corners of your document, and make sure that all information is visible."}
                  docType={currentDoc}
              />
          ),
        };
        break;
      }
      case DocType.GREEN_CARD_BACK: {
        results = {
          dragAndDropTitle: "Add the back of your ID.",
          example: (
              <document.GreenCardManualUploadExample
                  title={`Add the back side of ${pronoun} Green Card.`}
                  description={"We need to see all four corners of your document, and make sure that all information is visible."}
                  docType={currentDoc}
              />
          ),
        };
        break;
      }
    }
    return (
        <HeaderAndFooter>
          <div>
            <div className={"ManualUpload-body"}>
              {results.example}
            </div>
            <div className={"ManualUpload-body"}>
              <DragAndDrop
                  accepted={false}
                  compWidth="xsmall-full small-half"
                  innerTitle={results.dragAndDropTitle}
                  innerSubTitle="Files must be JPG, PNG or PDF and smaller than 25MB."
                  onDrop={this.uploadDocument.bind(this, currentDoc)}
              />
            </div>
            {loader}
            {error}
          </div>
        </HeaderAndFooter>
    );
  }
}



// WEBPACK FOOTER //
// ./src/manual-upload/ManualUpload.tsx